import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { TreeNode } from "../../components/TreeView/types";
import { FileItem } from "../../components/FileExplorer/types";

interface NavigationState {
    path: string[];
    node: TreeNode | null;
}

interface FileExplorerState {
    viewMode: "grid" | "list";
    navigationState: NavigationState;
    searchQuery: string;
    selectedItems: string[];
    filteredContent: TreeNode[];
    currentContent: FileItem[];
    isLoading: boolean;
    error: string | null;
}

const initialState: FileExplorerState = {
    viewMode: "grid",
    navigationState: {
        path: [],
        node: null,
    },
    searchQuery: "",
    selectedItems: [],
    filteredContent: [],
    currentContent: [],
    isLoading: false,
    error: null,
};

const fileExplorerSlice = createSlice({
    name: "fileExplorer",
    initialState,
    reducers: {
        setViewMode: (state, action: PayloadAction<"grid" | "list">) => {
            state.viewMode = action.payload;
        },
        setNavigationState: (state, action: PayloadAction<NavigationState>) => {
            state.navigationState = action.payload;
        },
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<string[]>) => {
            state.selectedItems = action.payload;
        },
        setFilteredContent: (state, action: PayloadAction<TreeNode[]>) => {
            state.filteredContent = action.payload;
        },
        setCurrentContent: (state, action: PayloadAction<FileItem[]>) => {
            state.currentContent = action.payload;
        },
        setIsLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        toggleItemSelection: (state, action: PayloadAction<string>) => {
            const itemId = action.payload;
            const index = state.selectedItems.indexOf(itemId);
            if (index === -1) {
                state.selectedItems.push(itemId);
            } else {
                state.selectedItems.splice(index, 1);
            }
        },
        navigateToFolder: (state, action: PayloadAction<TreeNode>) => {
            const node = action.payload;

            // Prevent navigation to the same folder
            if (state.navigationState.node?.id === node.id) return;

            // Find if this node is already in the current path
            const existingIndex = state.navigationState.path.indexOf(node.id);

            if (existingIndex !== -1) {
                // If we're navigating to a node that's already in our path,
                // trim the path up to that point
                state.navigationState.path = state.navigationState.path.slice(0, existingIndex + 1);
                state.navigationState.node = node;
            } else {
                state.navigationState.path = [...state.navigationState.path, node.id];
                state.navigationState.node = node;
            }

            if (node.children) {
                state.filteredContent = node.children;
            } else {
                state.filteredContent = [];
            }
        },
        navigateUp: (state) => {
            if (state.navigationState.path.length > 0) {
                const newPath = state.navigationState.path.slice(0, -1);
                state.navigationState.path = newPath;
                // Note: We'll need to handle finding the parent node in the hook
            }
        },
        navigateToPathSegment: (state, action: PayloadAction<number>) => {
            const index = action.payload;
            state.navigationState.path = state.navigationState.path.slice(0, index + 1);
            // Note: We'll need to handle finding the target node in the hook
        },
    },
});

export const {
    setViewMode,
    setNavigationState,
    setSearchQuery,
    setSelectedItems,
    setFilteredContent,
    setCurrentContent,
    setIsLoading,
    setError,
    toggleItemSelection,
    navigateToFolder,
    navigateUp,
    navigateToPathSegment,
} = fileExplorerSlice.actions;

export default fileExplorerSlice.reducer;
